import React from 'react';
import { PatientsForm } from '../../usecases/legacy/form';
import { FiEdit2 } from 'react-icons/fi';
import { usePortalStore } from '../../../../stores/portalStore';
import { Modal } from '../../../../shared/ui/Modal';
import {
    Alert,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    SvgIcon,
    Switch,
    TextField,
} from '@mui/material';
import { usePatientsStore } from '../../store/patientsStore';
import { Tooltip } from '../../../../shared/ui/Tooltip';
import { colors } from '../../../../app/theme/core/colors';
import { sendPatientSensitiveChangeRequest } from '../../../../services/patients/patientService';

type EditPatientPopupProps = {
    open: boolean;
    handleClose: () => void;
    handleSave: () => void | Promise<void>;
};

// ─── Título de seção com separador, igual ao UserForm ────────────────────────
const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h3
        style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#444',
            margin: '0 0 15px 0',
            paddingBottom: '8px',
            borderBottom: '1px solid #e8e8e8',
        }}
    >
        {children}
    </h3>
);

const normalizeDigits = (value: unknown) => String(value ?? '').replace(/\D/g, '');
const normalizeText = (value: unknown) => String(value ?? '').trim();
const isEnabledFlag = (value: unknown) => value === true || value === 1 || value === '1';
const normalizeComparableText = (value: unknown) => {
    const text = normalizeText(value)
        .replace(/[.,;:/\\|()[\]{}_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    return text
        .replace(/^(r|r\.|rua)\s+/, 'rua ')
        .replace(/^(av|av\.|avenida)\s+/, 'avenida ')
        .replace(/^(rod|rod\.|rodovia)\s+/, 'rodovia ')
        .replace(/^(estr|estr\.|estrada)\s+/, 'estrada ')
        .replace(/^(trav|trav\.|travessa)\s+/, 'travessa ')
        .replace(/\s+/g, ' ')
        .trim();
};
const normalizeStatus = (value: unknown) => normalizeText(value).toLowerCase();
const IDENTITY_CHANGE_KEYS = new Set(['name', 'cpf']);
const ADDRESS_CHANGE_KEYS = new Set(['cep', 'city', 'uf', 'address', 'number', 'neighborhood', 'addressLine2']);

const isValidBirthDate = (value: unknown) => {
    const text = normalizeText(value);
    const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const brMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!isoMatch && !brMatch) return false;

    const year = Number(isoMatch?.[1] ?? brMatch?.[3]);
    const month = Number(isoMatch?.[2] ?? brMatch?.[2]);
    const day = Number(isoMatch?.[3] ?? brMatch?.[1]);
    const date = new Date(year, month - 1, day);
    const today = new Date();

    return (
        year > 0 &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day &&
        year * 10000 + month * 100 + day <=
            today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate()
    );
};

const formatBirthDate = (value: unknown) => {
    const text = normalizeText(value);
    const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return isoMatch ? `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}` : text;
};

const maskBirthDateInput = (value: unknown) => {
    const digits = normalizeDigits(value).slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const SENSITIVE_FIELDS = [
    { key: 'name', label: 'Nome completo', normalize: normalizeComparableText },
    { key: 'cpf', label: 'CPF', normalize: normalizeDigits },
    { key: 'cnpj', label: 'Empresa / CNPJ', normalize: normalizeDigits },
    { key: 'cellphone', label: 'Telefone 1', normalize: normalizeDigits },
    { key: 'phoneNumber', label: 'Telefone 2', normalize: normalizeDigits },
    { key: 'cep', label: 'CEP', normalize: normalizeDigits },
    { key: 'city', label: 'Cidade', normalize: normalizeComparableText },
    { key: 'uf', label: 'UF', normalize: normalizeComparableText },
    { key: 'address', label: 'Endereço', normalize: normalizeComparableText },
    { key: 'number', label: 'Número', normalize: normalizeComparableText },
    { key: 'neighborhood', label: 'Bairro', normalize: normalizeComparableText },
    { key: 'addressLine2', label: 'Complemento', normalize: normalizeComparableText },
];

const stringifyErrorDetail = (value: unknown) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value);
    }
};

const getSensitiveChangeRequestError = (response: any) => {
    const body = response?.body ?? response?.data ?? response;
    const message = body?.message || response?.message || '';
    const details = body?.details || '';
    const emailStatus = body?.emailStatus ? `HTTP ${body.emailStatus}` : '';
    const emailResponse = stringifyErrorDetail(body?.emailResponse);
    const parts = [message, details && details !== message ? details : '', emailStatus, emailResponse].filter(Boolean);

    return parts.join(' | ') || 'Não foi possível enviar a solicitação.';
};

export const EditPatientModal: React.FC<EditPatientPopupProps> = ({ open, handleClose, handleSave }) => {
    const currentUser = usePortalStore((state: any) => state.currentUser);
    const newPat = usePatientsStore((state) => state.newPat);

    const [editModeOn, setEditModeOn] = React.useState(false);
    const [requestPreviewOpen, setRequestPreviewOpen] = React.useState(false);
    const [requestSubmitting, setRequestSubmitting] = React.useState(false);
    const [requestError, setRequestError] = React.useState('');
    const [requestSuccess, setRequestSuccess] = React.useState('');
    const [saveSubmitting, setSaveSubmitting] = React.useState(false);
    const [identityConfirmed, setIdentityConfirmed] = React.useState(false);
    const [addressConfirmed, setAddressConfirmed] = React.useState(false);
    const [cepReference, setCepReference] = React.useState<Record<string, string> | null>(null);
    const [requestStep, setRequestStep] = React.useState(0);
    const successCloseTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const applyingCepLookupRef = React.useRef(false);
    const originalValuesRef = React.useRef<Record<string, string> | null>(null);
    const originalStatusRef = React.useRef<string | null>(null);

    const onPaymentList = usePatientsStore((state) => state.onPaymentList);
    const setOnPaymentList = usePatientsStore((state) => state.setOnPaymentList);
    const name = usePatientsStore((state) => state.name);
    const sap = usePatientsStore((state) => state.sap);
    const cpf = usePatientsStore((state) => state.cpf);
    const birthDate = usePatientsStore((state) => state.birthDate);
    const cnpj = usePatientsStore((state) => state.cnpj);
    const cep = usePatientsStore((state) => state.cep);
    const city = usePatientsStore((state) => state.city);
    const uf = usePatientsStore((state) => state.uf);
    const address = usePatientsStore((state) => state.address);
    const addressLine2 = usePatientsStore((state) => state.addressLine2);
    const neighborhood = usePatientsStore((state) => state.neighborhood);
    const cellphone = usePatientsStore((state) => state.cellphone);
    const phoneNumber = usePatientsStore((state) => state.phoneNumber);
    const number = usePatientsStore((state) => state.number);
    const status = usePatientsStore((state) => state.status);
    const getCEP = usePatientsStore((state) => state.getCEP);
    const setBirthDate = usePatientsStore((state) => state.setBirthDate);
    const setCEP = usePatientsStore((state) => state.setCEP);
    const setCity = usePatientsStore((state) => state.setCity);
    const setUF = usePatientsStore((state) => state.setUF);
    const setAddress = usePatientsStore((state) => state.setAddress);
    const setAddressLine2 = usePatientsStore((state) => state.setAddressLine2);
    const setNeighborhood = usePatientsStore((state) => state.setNeighborhood);
    const setNumber = usePatientsStore((state) => state.setNumber);

    const canManage = currentUser.isAdmin || currentUser.isManager;
    const isWhiteMartinsOnly = currentUser.isWhiteMartins && !canManage;
    if (originalStatusRef.current === null) {
        originalStatusRef.current = String(status ?? '');
    }
    const isOriginallyRequested = normalizeStatus(originalStatusRef.current) === 'solicitado';
    const canEdit = canManage && !newPat;
    const canEditManagementFields = canManage && (editModeOn || newPat);
    const canEditPaymentList = Boolean(currentUser.isAdmin || isEnabledFlag(currentUser.canEditPaymentList));
    const showPaymentListToggle = canEditPaymentList;
    const formDisabled = isWhiteMartinsOnly || !canManage || (!editModeOn && !newPat);
    const currentSensitiveSnapshot = React.useMemo(
        () => ({
            name,
            cpf,
            birthDate,
            cnpj,
            cellphone,
            phoneNumber,
            cep,
            city,
            uf,
            address,
            number,
            neighborhood,
            addressLine2,
        }),
        [name, cpf, birthDate, cnpj, cellphone, phoneNumber, cep, city, uf, address, number, neighborhood, addressLine2]
    );

    if (originalValuesRef.current === null) {
        originalValuesRef.current = currentSensitiveSnapshot;
    }

    React.useEffect(() => {
        setEditModeOn(canManage && Boolean(newPat));
        setRequestPreviewOpen(false);
        setRequestSubmitting(false);
        setSaveSubmitting(false);
        setRequestError('');
        setRequestSuccess('');
        setIdentityConfirmed(false);
        setAddressConfirmed(false);
        setCepReference(null);
        setRequestStep(0);
        originalValuesRef.current = currentSensitiveSnapshot;
        originalStatusRef.current = String(status ?? '');
    }, [open]);

    React.useEffect(() => {
        return () => {
            if (successCloseTimeoutRef.current) {
                clearTimeout(successCloseTimeoutRef.current);
            }
        };
    }, []);

    React.useEffect(() => {
        setIdentityConfirmed(false);
        setRequestStep(0);
    }, [name, cpf, birthDate]);

    React.useEffect(() => {
        if (applyingCepLookupRef.current) return;

        setAddressConfirmed(false);
        setCepReference(null);
        setRequestStep(0);
    }, [cep, address, number, addressLine2, neighborhood, city, uf]);

    const getCurrentSensitiveValue = (fieldKey: string) => {
        switch (fieldKey) {
            case 'name':
                return name;
            case 'cpf':
                return cpf;
            case 'cnpj':
                return cnpj;
            case 'cellphone':
                return cellphone;
            case 'phoneNumber':
                return phoneNumber;
            case 'cep':
                return cep;
            case 'city':
                return city;
            case 'uf':
                return uf;
            case 'address':
                return address;
            case 'number':
                return number;
            case 'neighborhood':
                return neighborhood;
            case 'addressLine2':
                return addressLine2;
            default:
                return '';
        }
    };

    const sensitiveChanges = React.useMemo(() => {
        if (newPat || isOriginallyRequested) return [];

        return SENSITIVE_FIELDS.reduce((acc, field) => {
            const originalValue = originalValuesRef.current?.[field.key] ?? '';
            const nextValue = getCurrentSensitiveValue(field.key) ?? '';

            if (field.normalize(originalValue) !== field.normalize(nextValue)) {
                acc.push({
                    key: field.key,
                    label: field.label,
                    from: String(originalValue ?? ''),
                    to: String(nextValue ?? ''),
                });
            }

            return acc;
        }, [] as Array<{ key: string; label: string; from: string; to: string }>);
    }, [name, cpf, cnpj, cellphone, phoneNumber, cep, city, uf, address, number, neighborhood, addressLine2, newPat, isOriginallyRequested]);

    const hasSensitiveChanges = sensitiveChanges.length > 0;
    const hasIdentityChanges = sensitiveChanges.some((change) => IDENTITY_CHANGE_KEYS.has(change.key));
    const hasAddressChanges = sensitiveChanges.some((change) => ADDRESS_CHANGE_KEYS.has(change.key));
    const originalPatientName = originalValuesRef.current?.name ?? name;
    const identityConfirmation = hasIdentityChanges
        ? {
              confirmed: identityConfirmed,
              current: {
                  name: originalValuesRef.current?.name ?? '',
                  cpf: originalValuesRef.current?.cpf ?? '',
                  birthDate: formatBirthDate(originalValuesRef.current?.birthDate ?? ''),
              },
              proposed: {
                  name,
                  cpf,
                  birthDate: formatBirthDate(birthDate),
              },
          }
        : undefined;
    const addressConfirmation = hasAddressChanges
        ? {
              confirmed: addressConfirmed,
              current: {
                  cep: originalValuesRef.current?.cep ?? '',
                  address: originalValuesRef.current?.address ?? '',
                  number: originalValuesRef.current?.number ?? '',
                  complement: originalValuesRef.current?.addressLine2 ?? '',
                  neighborhood: originalValuesRef.current?.neighborhood ?? '',
                  city: originalValuesRef.current?.city ?? '',
                  uf: originalValuesRef.current?.uf ?? '',
              },
              proposed: {
                  cep,
                  address,
                  number,
                  complement: addressLine2,
                  neighborhood,
                  city,
                  uf,
              },
              cepReference: cepReference ?? undefined,
          }
        : undefined;
    const cepDivergences = React.useMemo(() => {
        if (!cepReference || !addressConfirmation) return [];

        const comparisonFields: Array<[
            string,
            unknown,
            unknown,
            (value: unknown) => string,
        ]> = [
            ['CEP', cepReference.cep, addressConfirmation.proposed.cep, normalizeDigits],
            ['Rua', cepReference.address, addressConfirmation.proposed.address, normalizeComparableText],
            ['Bairro', cepReference.neighborhood, addressConfirmation.proposed.neighborhood, normalizeComparableText],
            ['Cidade', cepReference.city, addressConfirmation.proposed.city, normalizeComparableText],
            ['UF', cepReference.uf, addressConfirmation.proposed.uf, normalizeComparableText],
        ];

        return comparisonFields
            .filter(([, reference, proposed, normalize]) => normalize(reference) !== normalize(proposed))
            .map(([label]) => String(label));
    }, [cepReference, addressConfirmation]);
    const requestSteps = React.useMemo(
        () => [hasIdentityChanges ? 'identity' : null, hasAddressChanges ? 'address' : null, 'summary'].filter(
            Boolean
        ) as Array<'identity' | 'address' | 'summary'>,
        [hasIdentityChanges, hasAddressChanges]
    );
    const currentRequestStep = requestSteps[Math.min(requestStep, requestSteps.length - 1)] ?? 'summary';
    const isLastRequestStep = requestStep === requestSteps.length - 1;
    const primaryActionLabel = hasSensitiveChanges ? 'Solicitar alteração' : 'Salvar';

    const requestEmailSubject = `Solicitação de Alteração de dados do paciente - SAP ${sap || '-'}`;
    const requestEmailBody = React.useMemo(() => {
        const changes = sensitiveChanges
            .map((change) => `- ${change.label}: De "${change.from || '-'}" para "${change.to || '-'}"`)
            .join('\n');
        const identityLines = identityConfirmation
            ? [
                  '',
                  'Identificação para confirmação:',
                  `Nome: De "${identityConfirmation.current.name || '-'}" para "${identityConfirmation.proposed.name || '-'}"`,
                  `CPF: De "${identityConfirmation.current.cpf || '-'}" para "${identityConfirmation.proposed.cpf || '-'}"`,
                  `Data de nascimento: De "${identityConfirmation.current.birthDate || '-'}" para "${identityConfirmation.proposed.birthDate || '-'}"`,
              ]
            : [];
        const addressLines = addressConfirmation
            ? [
                  '',
                  'Endereço para confirmação:',
                  `CEP: ${addressConfirmation.proposed.cep || '-'}`,
                  `Rua: ${addressConfirmation.proposed.address || '-'}`,
                  `Número: ${addressConfirmation.proposed.number || '-'}`,
                  `Complemento: ${addressConfirmation.proposed.complement || '-'}`,
                  `Bairro: ${addressConfirmation.proposed.neighborhood || '-'}`,
                  `Cidade/UF: ${addressConfirmation.proposed.city || '-'} / ${addressConfirmation.proposed.uf || '-'}`,
              ]
            : [];

        return [
            'Olá,',
            '',
            'Solicito a avaliação para alteração de dados do paciente abaixo:',
            '',
            'Solicitação:',
            `Solicitante: ${currentUser.name || '-'}`,
            `E-mail do solicitante: ${currentUser.email || '-'}`,
            `CNPJ/Empresa: ${cnpj || '-'}`,
            '',
            'Paciente:',
            `Nome atual: ${originalPatientName || '-'}`,
            `SAP/ID White Martins: ${sap || '-'}`,
            ...identityLines,
            ...addressLines,
            '',
            'Alterações solicitadas:',
            changes || '- Nenhuma alteração identificada.',
        ].join('\n');
    }, [
        sensitiveChanges,
        identityConfirmation,
        addressConfirmation,
        originalPatientName,
        cnpj,
        sap,
        currentUser.name,
        currentUser.email,
    ]);

    const handlePrimaryAction = async () => {
        if (saveSubmitting) return;

        if (hasSensitiveChanges) {
            setRequestError('');
            setRequestSuccess('');
            setIdentityConfirmed(false);
            setAddressConfirmed(false);
            setCepReference(null);
            setRequestStep(0);
            setRequestPreviewOpen(true);
            return;
        }

        setSaveSubmitting(true);
        try {
            await handleSave();
        } finally {
            setSaveSubmitting(false);
        }
    };

    const closeRequestPreview = () => {
        if (requestSubmitting) return;
        setRequestPreviewOpen(false);
        setRequestError('');
        setRequestSuccess('');
        setIdentityConfirmed(false);
        setAddressConfirmed(false);
        setCepReference(null);
        setRequestStep(0);
    };

    const handleNextRequestStep = async () => {
        if (currentRequestStep === 'identity') {
            if (!normalizeText(name) || normalizeDigits(cpf).length !== 11 || !isValidBirthDate(birthDate)) {
                setRequestError('Preencha nome, CPF e data de nascimento válidos para confirmar a identificação.');
                return;
            }
        }

        if (currentRequestStep === 'address') {
            if (normalizeDigits(cep).length !== 8 || !normalizeText(number)) {
                setRequestError('Preencha CEP e número para confirmar o endereço.');
                return;
            }

            setRequestSubmitting(true);
            try {
                const result = await getCEP(cep);
                if (!result || result.erro) {
                    setRequestError('Informe um CEP válido para confirmar o endereço.');
                    return;
                }

                // Ao informar CEP no aviso, completa os campos consultados sem derrubar a confirmacao feita pelo usuario.
                applyingCepLookupRef.current = true;
                setCEP(result.cep ?? cep);
                setAddress(result.logradouro ?? address);
                setNeighborhood(result.bairro ?? neighborhood);
                setCity(result.localidade ?? city);
                setUF(result.uf ?? uf);
                setCepReference({
                    cep: result.cep ?? cep,
                    address: result.logradouro ?? '',
                    neighborhood: result.bairro ?? '',
                    city: result.localidade ?? '',
                    uf: result.uf ?? '',
                });
                setTimeout(() => {
                    applyingCepLookupRef.current = false;
                }, 0);
            } catch {
                applyingCepLookupRef.current = false;
                setRequestError('Não foi possível consultar o CEP. Tente novamente antes de enviar.');
                return;
            } finally {
                setRequestSubmitting(false);
            }
        }

        if (!isLastRequestStep) {
            setRequestError('');
            setRequestStep((current) => current + 1);
        }
    };

    const handleSendSensitiveChangeRequest = async () => {
        setRequestSubmitting(true);
        setRequestError('');
        setRequestSuccess('');

        try {
            const response = await sendPatientSensitiveChangeRequest({
                requesterName: currentUser.name || '',
                requesterEmail: currentUser.email || '',
                patientName: originalPatientName,
                patientCpf: cpf,
                patientCnpj: cnpj,
                patientSap: sap,
                identityConfirmation,
                addressConfirmation,
                changes: sensitiveChanges.map(({ key, label, from, to }) => ({ key, label, from, to })),
            });

            if (response?.httpStatus && response.httpStatus >= 400) {
                throw new Error(getSensitiveChangeRequestError(response));
            }

            setRequestSuccess('Solicitação enviada por e-mail.');
            successCloseTimeoutRef.current = setTimeout(() => {
                setRequestPreviewOpen(false);
                handleClose();
            }, 1200);
        } catch (error) {
            setRequestError(error instanceof Error ? error.message : 'Não foi possível enviar a solicitação.');
        } finally {
            setRequestSubmitting(false);
        }
    };

    return (
        <Modal open={open} handleClose={handleClose} width="55%">
            {/* ── Header ── */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                }}
            >
                <span
                    style={{
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#444',
                    }}
                >
                    {newPat ? 'Novo Paciente' : 'Paciente'}
                </span>
                {canEdit && (
                    <Tooltip title={editModeOn ? 'Cancelar edição' : 'Editar'}>
                        <IconButton sx={{ marginLeft: '12px' }} onClick={() => setEditModeOn(!editModeOn)} color="primary">
                            <SvgIcon fontSize="medium" htmlColor={colors.text.primary}>
                                <FiEdit2 className="hoverButton" />
                            </SvgIcon>
                        </IconButton>
                    </Tooltip>
                )}
            </div>

            {/* ── Corpo do formulário ── */}
            <div style={{ flex: 1 }}>
                {/* Seção de dados do paciente */}
                <SectionTitle>Dados do Paciente</SectionTitle>
                <PatientsForm
                    disabled={formDisabled}
                    canEditManagementFields={canEditManagementFields}
                    sensitiveFieldKeys={SENSITIVE_FIELDS.map((field) => field.key)}
                />

                {/* Switch de lista de pagamento — visível só no modo edição/criação */}
                {showPaymentListToggle && (
                    <div
                        style={{
                            marginTop: '16px',
                            paddingTop: '16px',
                            borderTop: '1px solid #e8e8e8',
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <FormControlLabel
                            control={<Switch checked={onPaymentList} onChange={(e) => setOnPaymentList(e.target.checked)} />}
                            label="Em lista de pagamento"
                            labelPlacement="start"
                        />
                    </div>
                )}
            </div>

            {/* ── Botões fixos no rodapé ── */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '15px',
                    marginTop: '20px',
                    borderTop: '1px solid #e8e8e8',
                }}
            >
                <Button variant="outlined" onClick={handleClose} disabled={saveSubmitting}>
                    Cancelar
                </Button>
                {(editModeOn || newPat) && canManage && (
                    <Button variant="contained" onClick={handlePrimaryAction} disabled={saveSubmitting}>
                        {saveSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : primaryActionLabel}
                    </Button>
                )}
            </div>
            <Dialog open={requestPreviewOpen} onClose={closeRequestPreview} maxWidth="md" fullWidth>
                <DialogTitle>
                    Solicitação de alteração de dados
                    <div style={{ fontSize: '13px', fontWeight: 400, color: '#666', marginTop: '4px' }}>
                        Etapa {requestStep + 1} de {requestSteps.length}
                    </div>
                </DialogTitle>
                <DialogContent>
                    {requestError ? (
                        <Alert severity="error" sx={{ marginBottom: '16px' }}>
                            {requestError}
                        </Alert>
                    ) : null}
                    {requestSuccess ? (
                        <Alert severity="success" sx={{ marginBottom: '16px' }}>
                            {requestSuccess}
                        </Alert>
                    ) : null}
                    <div style={{ display: 'grid', gap: '12px' }}>
                        {currentRequestStep === 'identity' && identityConfirmation ? (
                            <div>
                                <Alert severity="warning" sx={{ marginBottom: '8px' }}>
                                    Confira nome, CPF e data de nascimento antes de enviar.
                                </Alert>
                                <strong>Identificação para confirmação</strong>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(120px, 1fr) 1fr 1fr',
                                        gap: '6px 12px',
                                        marginTop: '8px',
                                        padding: '10px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '6px',
                                    }}
                                >
                                    <strong>Campo</strong><strong>Atual</strong><strong>Proposto</strong>
                                    <span>Nome</span><span>{identityConfirmation.current.name || '-'}</span><span>{identityConfirmation.proposed.name || '-'}</span>
                                    <span>CPF</span><span>{identityConfirmation.current.cpf || '-'}</span><span>{identityConfirmation.proposed.cpf || '-'}</span>
                                    <span>Data de nascimento</span>
                                    <span>{identityConfirmation.current.birthDate || '-'}</span>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="DD/MM/AAAA"
                                        value={formatBirthDate(birthDate)}
                                        onChange={(event) => {
                                            setRequestError('');
                                            setBirthDate(maskBirthDateInput(event.target.value));
                                        }}
                                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                        error={Boolean(birthDate) && !isValidBirthDate(birthDate)}
                                        helperText={
                                            Boolean(birthDate) && !isValidBirthDate(birthDate)
                                                ? 'Informe uma data válida.'
                                                : 'Obrigatória.'
                                        }
                                    />
                                </div>
                                <FormControlLabel
                                    control={<Checkbox checked={identityConfirmed} onChange={(e) => setIdentityConfirmed(e.target.checked)} />}
                                    label="Confirmei os dados de identificação"
                                />
                            </div>
                        ) : null}
                        {currentRequestStep === 'address' && addressConfirmation ? (
                            <div>
                                <Alert severity={cepDivergences.length > 0 ? 'warning' : 'info'} sx={{ marginBottom: '8px' }}>
                                    {!cepReference
                                        ? 'Informe CEP, número e complemento, quando houver, antes de confirmar.'
                                        : cepDivergences.length > 0
                                        ? `Há divergências no retorno do CEP: ${cepDivergences.join(', ')}. Confira antes de confirmar.`
                                        : 'O endereço informado corresponde ao retorno do CEP. Confira antes de confirmar.'}
                                </Alert>
                                <strong>Endereço para confirmação</strong>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'minmax(120px, 1fr) 1fr 1fr',
                                        gap: '6px 12px',
                                        marginTop: '8px',
                                        padding: '10px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '6px',
                                    }}
                                >
                                    <strong>Campo</strong><strong>CEP Atual</strong><strong>Proposto</strong>
                                    <span>CEP</span>
                                    <span>{addressConfirmation.current.cep || '-'}</span>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="CEP"
                                        value={cep}
                                        onChange={(event) => {
                                            setRequestError('');
                                            setAddressConfirmed(false);
                                            setCepReference(null);
                                            setCEP(event.target.value);
                                        }}
                                        inputProps={{ maxLength: 9, inputMode: 'numeric' }}
                                        error={Boolean(cep) && normalizeDigits(cep).length !== 8}
                                        helperText={
                                            Boolean(cep) && normalizeDigits(cep).length !== 8 ? 'Informe um CEP válido.' : ''
                                        }
                                    />

                                    <span>Rua</span><span>{addressConfirmation.current.address || '-'}</span><span>{addressConfirmation.proposed.address || '-'}</span>

                                    <span>Número</span>
                                    <span>{addressConfirmation.current.number || '-'}</span>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Número"
                                        value={number}
                                        onChange={(event) => {
                                            setRequestError('');
                                            setAddressConfirmed(false);
                                            setNumber(event.target.value);
                                        }}
                                        error={!normalizeText(number)}
                                        helperText={!normalizeText(number) ? 'Obrigatório.' : ''}
                                    />

                                    <span>Complemento</span>
                                    <span>{addressConfirmation.current.complement || '-'}</span>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Complemento"
                                        value={addressLine2}
                                        onChange={(event) => {
                                            setRequestError('');
                                            setAddressConfirmed(false);
                                            setAddressLine2(event.target.value);
                                        }}
                                    />

                                    <span>Bairro</span><span>{addressConfirmation.current.neighborhood || '-'}</span><span>{addressConfirmation.proposed.neighborhood || '-'}</span>
                                    <span>Cidade</span><span>{addressConfirmation.current.city || '-'}</span><span>{addressConfirmation.proposed.city || '-'}</span>
                                    <span>UF</span><span>{addressConfirmation.current.uf || '-'}</span><span>{addressConfirmation.proposed.uf || '-'}</span>
                                </div>
                                <FormControlLabel
                                    control={<Checkbox checked={addressConfirmed} onChange={(e) => setAddressConfirmed(e.target.checked)} />}
                                    label="Confirmei os dados de endereço"
                                />
                            </div>
                        ) : null}
                        {currentRequestStep === 'summary' ? (
                            <>
                                <Alert severity="info" sx={{ marginBottom: '4px' }}>
                                    Revise a solicitação completa antes do envio.
                                </Alert>
                                <div>
                                    <strong>Alterações solicitadas</strong>
                                    <div style={{ display: 'grid', gap: '8px', marginTop: '8px' }}>
                                        {sensitiveChanges.map((change) => (
                                            <div
                                                key={change.key}
                                                style={{
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: '6px',
                                                    padding: '10px',
                                                    background: '#fafafa',
                                                }}
                                            >
                                                <div style={{ fontWeight: 600 }}>{change.label}</div>
                                                <div>De: {change.from || '-'}</div>
                                                <div>Para: {change.to || '-'}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <strong>Assunto</strong>
                                    <div style={{ marginTop: '6px' }}>{requestEmailSubject}</div>
                                </div>
                                <div>
                                    <strong>Corpo do e-mail</strong>
                                    <pre
                                        style={{
                                            whiteSpace: 'pre-wrap',
                                            background: '#f7f7f7',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '6px',
                                            padding: '12px',
                                            marginTop: '6px',
                                            maxHeight: '280px',
                                            overflow: 'auto',
                                        }}
                                    >
                                        {requestEmailBody}
                                    </pre>
                                </div>
                            </>
                        ) : null}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRequestPreview} disabled={requestSubmitting}>
                        Fechar
                    </Button>
                    <div style={{ flex: 1 }} />
                    {requestStep > 0 ? (
                        <Button
                            onClick={() => setRequestStep((current) => current - 1)}
                            disabled={requestSubmitting}
                        >
                            Voltar
                        </Button>
                    ) : null}
                    {currentRequestStep !== 'summary' ? (
                        <Button
                            variant="contained"
                            onClick={handleNextRequestStep}
                            disabled={
                                requestSubmitting ||
                                Boolean(requestError) ||
                                (currentRequestStep === 'identity' && !identityConfirmed) ||
                                (currentRequestStep === 'address' && !addressConfirmed)
                            }
                        >
                            {requestSubmitting ? 'Consultando...' : 'Continuar'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleSendSensitiveChangeRequest}
                            disabled={requestSubmitting || Boolean(requestSuccess) || Boolean(requestError)}
                        >
                            {requestSubmitting ? 'Enviando...' : 'Enviar solicitação'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Modal>
    );
};
